import { Payment, SalesDetail } from "../../../../interfaces/sales";
import { PaymentField } from "../interfaces";
import { transalateTransactionStatus } from "../../../../translates/transactions/transactions";
import { hexaToDec } from "../../../../utils/nfcHexaTranslate";
import { formatCardDate, formatDate } from "../../../../utils/dates";
import { formatCurrency } from "../../../../utils/moneyFormat";
import { UTC_PAID_AT_MERCHANTS } from "../../../../utils/sales";
import { getCardNumbersFormat } from "./getCardNumbersFormat";
import { getQrTransactionInfo } from "./qrTransaction";

/**
 * One payment card of the sale detail: the header the screen shows (title, code, status and total)
 * plus its two columns of fields.
 *
 * YOSHI-10444: this is the single source of truth for those fields. The screen renders it through
 * `PaymentDetail` and the PDF prints the same sections, so a field added here shows up in both.
 */
export interface SalePaymentSection {
  title: string;
  code: string;
  status: number;
  total: number;
  fields: {
    left: PaymentField[];
    right: PaymentField[];
  };
}

/** Label of a transaction status, matching what the payment header shows. */
export const getPaymentStatusLabel = (status: number): string =>
  transalateTransactionStatus[status] || "Desconocido";

/**
 * Renders a field the way `PaymentDetailField` renders it on screen: money and dates formatted,
 * an absent value as a dash. Shared so the PDF never formats a value differently than the screen.
 */
export const formatPaymentFieldValue = (
  value: string | number,
  type: PaymentField["type"] = "text",
): string => {
  if (typeof value === "undefined" || value === null || value === "") {
    return "-";
  }
  if (type === "money") {
    return formatCurrency(value as number);
  }
  if (type === "date") {
    return formatCardDate(value.toString());
  }
  if (type === "utcDate") {
    return formatDate(value.toString(), "dd'/'MM'/'yyyy ' ' HH:mm 'hrs.'");
  }
  // YOSHI-10935: a file is opened from the screen; on paper only its presence is stated
  if (type === "file") {
    return "Adjunto";
  }
  return value.toString();
};

/** Fields the screen hides: a conditional field with no value is never rendered. */
export const getVisibleFields = (fields: PaymentField[]): PaymentField[] =>
  fields.filter((field) => !(field.isConditional && !field.value));

/**
 * Builds the payment sections of a sale. A payment carries one transaction, so it normally yields a
 * single section; the list keeps the same order the screen shows.
 */
export const getSalePaymentSections = (
  sale: SalesDetail,
): SalePaymentSection[] => {
  const sections: SalePaymentSection[] = [];

  (sale.payments || []).forEach((item: Payment) => {
    const header = {
      code: item.code,
      status: item.status,
      total: item.total,
    };

    if (item.nfc_transaction) {
      sections.push({
        ...header,
        title: "Pago con Tarjeta Cashless",
        fields: {
          left: [
            { label: "Fecha", value: item.paid_at, type: "date" },
            {
              label: "Número de la Tarjeta",
              value: `${hexaToDec(
                item.nfc_transaction.nfc_card.serial_number,
              )} / ${item.nfc_transaction.nfc_card.serial_number}`,
            },
            {
              label: "Saldo Pre-venta",
              value: item.nfc_transaction.previous_balance,
              type: "money",
            },
            { label: "Subtotal", value: item.subtotal, type: "money" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            {
              label: "Identificador de Terminal",
              value: "-",
              isCopyable: true,
            },
            {
              label: "Saldo Post-venta",
              value: item.nfc_transaction.post_balance,
              type: "money",
            },
            { label: "Propina", value: item.tip, type: "money" },
          ],
        },
      });
    }

    if (item.card_transaction) {
      sections.push({
        ...header,
        title: "Pago con Tarjeta Bancaria",
        fields: {
          left: [
            {
              label: "Fecha",
              value: item.paid_at,
              type: UTC_PAID_AT_MERCHANTS.includes(
                item.card_transaction.merchant?.toLowerCase() ?? "",
              )
                ? "utcDate"
                : "date",
            },
            { label: "Marca", value: item.card_transaction.card.brand },
            { label: "Banco", value: item.card_transaction.card.bank },
            {
              label: "Número de la Tarjeta",
              value: getCardNumbersFormat(
                item.card_transaction.card.bin,
                item.card_transaction.card.last_four,
              ),
              type: "cardNumber",
            },
            { label: "Tipo de tarjeta", value: item.card_transaction.card.type },
            { label: "Medio", value: item.card_transaction.entry_mode },
            { label: "Subtotal", value: item.subtotal, type: "money" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            {
              label: "Identificador de Terminal",
              value: item.card_transaction.terminal,
              isCopyable: true,
            },
            {
              label: "Código de Autorización",
              value: item.card_transaction.authorization,
              info: "Código único generado por el banco emisor que confirma la autorización de la transacción",
            },
            {
              label: "Código de Operación",
              value: item.card_transaction.operation_key,
              info: "Identificador único de la operación bancaria que facilita el seguimiento de la transacción",
            },
            {
              label: "Código de Transacción",
              value: item.card_transaction.transaction_id,
              info: "Número de referencia asignado por el procesador de pagos para identificar la transacción en su sistema",
            },
            { label: "Código de Venta", value: item.card_transaction.sale_key },
            {
              label: "Reembolso",
              value: item.card_transaction.has_refund ? "Sí" : "",
              isConditional: true,
            },
            {
              label: "Razón de rechazo",
              value: item.card_transaction.rejection_reason,
              isConditional: true,
            },
            { label: "Propina", value: item.tip, type: "money" },
          ],
        },
      });
    }

    if (item.qr_transaction) {
      sections.push({
        ...header,
        title: "Pago con QR",
        fields: {
          left: [
            { label: "Fecha", value: item.paid_at, type: "date" },
            {
              label: item.qr_transaction.qr_ticket.uuid ? "Ticket" : "Cliente",
              value: getQrTransactionInfo(item.qr_transaction),
              isCopyable: !!item.qr_transaction.qr_ticket.uuid,
            },
            { label: "Subtotal", value: item.subtotal, type: "money" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            {
              label: "Identificador de Terminal",
              value: item.qr_transaction.terminal,
              isCopyable: true,
            },
            { label: "Propina", value: item.tip, type: "money" },
          ],
        },
      });
    }

    if (item.palm_transaction) {
      sections.push({
        ...header,
        title: "Pago con Mano",
        fields: {
          left: [
            { label: "Fecha", value: item.paid_at, type: "date" },
            {
              label: "Cliente",
              value:
                item.palm_transaction.palm_fingerprint?.customer
                  ?.phone_number || "-",
              isCopyable: true,
            },
            { label: "Subtotal", value: item.subtotal, type: "money" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            {
              label: "Identificador de Terminal",
              value: item.palm_transaction.terminal,
              isCopyable: true,
            },
            { label: "Propina", value: item.tip, type: "money" },
          ],
        },
      });
    }

    // YOSHI-10935: the transfer is not processed by Yoshi, so what matters is what identifies it with
    // the bank and who vouched that the money arrived
    if (item.spei_transaction) {
      const transfer = item.spei_transaction;
      const registeredBy: string = transfer.source === "external_order"
        ? `API de órdenes externas${transfer.api_client_name ? ` (${transfer.api_client_name})` : ""}`
        : transfer.user_registered_name || "-";
      sections.push({
        ...header,
        title: "Pago por SPEI",
        fields: {
          left: [
            { label: "Clave de rastreo", value: transfer.tracking_key, isCopyable: true },
            { label: "Monto transferido", value: transfer.amount, type: "money" },
            { label: "Total de la venta", value: sale.total, type: "money" },
            { label: "Fecha de la transferencia", value: transfer.operation_at, type: "utcDate" },
            { label: "Banco origen", value: transfer.origin_bank ?? "" },
            { label: "Ordenante", value: transfer.sender_name ?? "" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            { label: "Referencia / concepto", value: transfer.reference ?? "" },
            { label: "Registrado por", value: registeredBy },
            { label: "Fecha de registro", value: transfer.registered_at, type: "utcDate" },
            { label: "Propina", value: item.tip, type: "money" },
            { label: "Comprobante", value: transfer.receipt_url ?? "", type: "file", isConditional: true },
          ],
        },
      });
    }

    if (item.cash_transaction) {
      sections.push({
        ...header,
        title: "Pago en Efectivo",
        fields: {
          left: [
            { label: "Fecha", value: item.paid_at, type: "date" },
            { label: "Subtotal", value: item.subtotal, type: "money" },
          ],
          right: [
            { label: "ID de Pago", value: item.uuid, isCopyable: true },
            {
              label: "Identificador de Terminal",
              value: "-",
              isCopyable: true,
            },
            { label: "Propina", value: item.tip, type: "money" },
          ],
        },
      });
    }
  });

  return sections;
};
